import { useMemo } from 'react';
import styled from 'styled-components';
import useI18n from '../../../i18n';
import Button from '../../../Components/Button';
import getImageUrlWithSize from '../../../utils/getImageUrlWithSize';
import { DEVICE_SIZES, useDeviceSize } from '../../../Hooks/useDeviceSize';
import { useConfig } from '../../../Components/ConfigProvider';

const Wrapper = styled.div``;

const Hero = styled.div`
  height: 660px;
  padding: 104px 24px 48px;
  background-size: cover;
  background-position: center;
  display: flex;
  flex-flow: column nowrap;
  background-color: #f5f5f8;
  z-index: 1;
  position: relative;
`;

const HeroImage = styled.div<{ image: any }>`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-image: url(${({ image }) => image});
  background-size: cover;
  background-position: center;
  z-index: -1;
  opacity: ${({ image }) => (image ? '1' : '0')};
  transition: opacity 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 56px;
  text-transform: uppercase;
  color: #ffffff;
  margin-top: 24px;
  word-break: break-word;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);

  @media (min-width: 992px) {
    text-align: center;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: auto;

  button {
    width: 100%;

    @media (min-width: 768px) {
      width: 435px;
      margin: auto auto 0 auto;
    }

    @media (min-width: 992px) {
      width: 500px;
    }
  }
`;

const IntroWrapper = styled.div`
  padding: 64px 24px;
  background-color: ${({ theme }) => theme.main || '#000'};
  font-size: 24px;
  font-weight: normal;
  letter-spacing: 0;
  line-height: 32px;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);

  p {
    color: ${({ theme }) => theme.contrastingColor || '#fff'};
    margin-bottom: 16px;
  }
`;

const IntroInner = styled.div`
  @media (min-width: 768px) {
    width: 435px;
    margin: 0 auto;
  }

  @media (min-width: 992px) {
    width: 500px;
    margin: 0 auto;
  }
`;

const IntroTitle = styled.h2`
  font-size: 40px;
  font-weight: 600;
  line-height: 56px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.contrastingColor || '#fff'};
`;

type Props = {
  /**
   * content
   */
  content: any; // needs to be changed in the backend
  /**
   * onNavigate
   */
  onNavigate: () => void;
  /**
   * isSignedIn
   */
  isSignedIn: boolean;
};

/**
 * IntroSection component
 */
const IntroSection = ({ content, isSignedIn, onNavigate }: Props) => {
  const { i18n } = useI18n();
  const config = useConfig();
  const title = content && content.title?.length > 1 ? i18n(content.title) : '';
  const oldIntroTitle = content ? i18n(content.subTitle) : '';
  const oldIntrotext = content ? i18n(content.text) : '';
  const image = content ? content.image.file.url : '';
  const deviceSize = useDeviceSize();

  const heroImage = useMemo(() => {
    if (image.length > 0 && deviceSize === DEVICE_SIZES.SMALL) {
      // Phone and deprecated content image
      return getImageUrlWithSize(image, window.innerWidth * 2, 1320);
    } else if (deviceSize === DEVICE_SIZES.SMALL) {
      // Phone and new image
      return config.LANDING_BACKGROUND_URL;
    } else {
      // Desktop
      if (config.LANDING_DESKTOP_BACKGROUND_URL) {
        return config.LANDING_DESKTOP_BACKGROUND_URL;
      } else {
        return image ?? config.LANDING_BACKGROUND_URL;
      }
    }
  }, [deviceSize, config.LANDING_BACKGROUND_URL]);

  const introTitleToUse = config.LANDING_PAGE_INTRO?.title
    ? i18n(config.LANDING_PAGE_INTRO?.title) || oldIntroTitle
    : oldIntroTitle;
  const introTextToUse = config.LANDING_PAGE_INTRO?.description
    ? i18n(config.LANDING_PAGE_INTRO?.description) || oldIntrotext
    : oldIntrotext;

  return (
    <Wrapper>
      <Hero>
        <HeroImage image={heroImage} />
        <Title>{title}</Title>
        <ButtonWrapper>
          <Button handleClick={onNavigate} text={i18n('Landingpage.OrderFood')} withArrow={true} />
        </ButtonWrapper>
      </Hero>
      {!isSignedIn && (
        <IntroWrapper>
          <IntroInner>
            <IntroTitle>{introTitleToUse}</IntroTitle>
            <div dangerouslySetInnerHTML={{ __html: i18n(introTextToUse) }} />
          </IntroInner>
        </IntroWrapper>
      )}
    </Wrapper>
  );
};

export default IntroSection;
